import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import { Badge } from '@/components/ui/badge'
import { Search } from 'lucide-react'

interface SearchResult {
  id: string
  title: string
  slug: string
  directory: string
  tags: string[]
  headline: string
  updated_at: string
}

interface Props {
  results: SearchResult[]
  query: string
  tag: string | null
  total_count: number
}

export default function SearchIndex({ results, query, tag, total_count }: Props) {
  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Search className="h-5 w-5 text-gray-400" />
          <h1 className="text-2xl font-bold">Search Results</h1>
        </div>

        {query && (
          <p className="text-sm text-gray-500">
            {total_count} result{total_count !== 1 ? 's' : ''} for "{query}"
            {tag && (
              <>
                {' '}in tag{' '}
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => router.visit(`/search?q=${encodeURIComponent(query)}`)}
                >
                  {tag} ×
                </Badge>
              </>
            )}
          </p>
        )}

        {tag && !query && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Filtered by tag:</span>
            <Badge
              variant="secondary"
              className="cursor-pointer"
              onClick={() => router.visit('/search')}
            >
              {tag} ×
            </Badge>
          </div>
        )}

        {results.length === 0 ? (
          <p className="text-sm text-gray-500">
            {query ? `No results found for "${query}".` : 'Enter a search query to find pages.'}
          </p>
        ) : (
          <div className="space-y-4">
            {results.map(result => (
              <div
                key={result.id}
                className="rounded-lg border border-gray-200 bg-white p-4 cursor-pointer hover:border-gray-300 transition-colors"
                onClick={() => router.visit(`/notes/${result.slug}`)}
              >
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-lg font-semibold text-gray-900">{result.title}</h2>
                  <Badge variant="outline" className="text-xs">{result.directory}</Badge>
                </div>
                <p
                  className="text-sm text-gray-500 mb-2 [&_mark]:bg-yellow-100 [&_mark]:text-yellow-800 [&_mark]:rounded [&_mark]:px-0.5"
                  dangerouslySetInnerHTML={{ __html: result.headline }}
                />
                <div className="flex items-center gap-2">
                  {result.tags.map(t => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="text-xs cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation()
                        router.visit(`/search?q=${encodeURIComponent(query)}&tag=${encodeURIComponent(t)}`)
                      }}
                    >
                      {t}
                    </Badge>
                  ))}
                  <span className="text-xs text-gray-400 ml-auto">
                    {new Date(result.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  )
}
